import { createSelector } from '@reduxjs/toolkit'
import { shallowEqual } from 'react-redux'
import type { RootState } from '@/store/store'
import { kAgentV2InitialTranscriptState, type AgentV2Message } from '@/agent-v2/store/reducer'
import { MatterArtifactVersionStatus, type MatterArtifactVersionRead } from '@/matter-artifacts/schemas'

type ConversationSelectorProps = { conversationId: string | undefined }

export const selectAgentV2Transcript = (state: RootState, props: ConversationSelectorProps) =>
  state.agentV2TranscriptState.transcriptsByConversationId[props.conversationId ?? ''] ?? kAgentV2InitialTranscriptState

export const selectAgentV2TranscriptMessages = (state: RootState, props: ConversationSelectorProps) => selectAgentV2Transcript(state, props).messages

// Attachment differences depend on user turns, not on the text streaming into assistant messages.
export const selectAgentV2UserMessages = createSelector(
  [selectAgentV2TranscriptMessages],
  (messages) => messages.filter((message) => message.role === 'user'),
  {
    memoizeOptions: {
      resultEqualityCheck: (previous: AgentV2Message[], next: AgentV2Message[]) =>
        previous.length === next.length && previous.every((message, index) => shallowEqual(message, next[index])),
    },
  }
)

export const selectAgentV2TranscriptError = (state: RootState, props: ConversationSelectorProps) => selectAgentV2Transcript(state, props).errorMessage

export const selectAgentV2StreamingMessageId = (state: RootState, props: ConversationSelectorProps) => selectAgentV2Transcript(state, props).streamingMessageId

export const selectAgentV2ActiveAssistantMessageId = (state: RootState, props: ConversationSelectorProps) =>
  selectAgentV2Transcript(state, props).activeAssistantMessageId

/**
 * Select: the newest complete version of each artifact the transcript links to. Every other artifact version in the
 * transcript is outdated.
 */
export const selectAgentV2LatestMatterArtifactVersionIds = createSelector(
  [selectAgentV2TranscriptMessages, (state: RootState) => state.matterArtifactsState.versionsById],
  (messages, matterArtifactVersionsById) => {
    const latestVersionByArtifactId = new Map<string, MatterArtifactVersionRead>()

    for (const message of messages) {
      for (const matterArtifactVersionId of message.matterArtifactVersionIds ?? []) {
        const matterArtifactVersion = matterArtifactVersionsById[matterArtifactVersionId]
        // A version still generating doesn't replace the one before it until it completes.
        if (matterArtifactVersion?.status !== MatterArtifactVersionStatus.COMPLETE) continue

        const latestVersion = latestVersionByArtifactId.get(matterArtifactVersion.matter_artifact_id)
        if (!latestVersion || matterArtifactVersion.created_at >= latestVersion.created_at) {
          latestVersionByArtifactId.set(matterArtifactVersion.matter_artifact_id, matterArtifactVersion)
        }
      }
    }

    return new Set([...latestVersionByArtifactId.values()].map((matterArtifactVersion) => matterArtifactVersion.id))
  },
  {
    memoizeOptions: {
      resultEqualityCheck: (previous: Set<string>, next: Set<string>) => previous.size === next.size && [...previous].every((id) => next.has(id)),
    },
  }
)
